<template>
  <div class="student-tcf-print">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ $t("Student Admission Form") }}</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ records.length }} {{ $t("Student(s)") }}
        </b-badge>
      </div>
      <div class="action-buttons">
        <button
          type="button"
          class="btn-modern btn-print"
          :disabled="downloading || loading || !records.length"
          @click.stop.prevent="downloadPdf"
        >
          <b-spinner v-if="downloading" small class="mr-50" />
          <feather-icon v-else icon="DownloadIcon" size="18" class="mr-50" />
          <span>{{ downloading ? $t("Downloading…") : $t("Download") }}</span>
        </button>
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>{{ $t("Print Report") }}</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>{{ $t("Close") }}</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">{{ $t("Loading student records…") }}</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadAll">
        {{ $t("Retry") }}
      </b-button>
    </div>

    <div v-else class="print-stack">
      <div
        v-for="(record, index) in records"
        :key="'student-' + (record.student.id || index)"
        class="admission-sheet"
      >
        <div class="print-page print-page--one">
          <header class="letterhead">
            <div class="letterhead__row">
              <div class="letterhead__logo-box">
                <img
                  v-if="record.campus.logo"
                  :src="imagePreviewUrl(record.campus.logo)"
                  alt="School logo"
                  class="letterhead__logo-img"
                />
                <div v-else class="letterhead__logo-placeholder">
                  <feather-icon icon="AwardIcon" size="28" />
                </div>
              </div>
              <div class="letterhead__school">
                <p class="letterhead__school-name">{{ record.campus.name }}</p>
                <p
                  v-if="record.campus.branch"
                  class="letterhead__school-branch"
                >
                  {{ record.campus.branch }}
                </p>
              </div>
            </div>
            <h1 class="letterhead__title">
              {{ $t("STUDENT ADMISSION FORM") }}
            </h1>
            <div class="letterhead__divider"></div>
          </header>

          <div class="meta-row">
            <!-- <div class="field-cell field-cell--half">
              <span class="field-cell__label">{{ $t("GR No") }}</span>
              <span class="field-cell__value">{{
                val(record.student.grNo)
              }}</span>
            </div> -->
            <div class="field-cell field-cell--half">
              <span class="field-cell__label">{{
                $t("Date of Admission")
              }}</span>
              <span class="field-cell__value">{{
                formatDate(record.student.date_of_admission)
              }}</span>
            </div>
          </div>

          <div class="form-block">
            <div class="form-block__content">
              <div class="fields-grid fields-grid--mixed">
                <div
                  v-for="field in record.basicFields"
                  :key="'basic-' + field.key"
                  class="field-cell"
                  :class="fieldCellClass(field)"
                >
                  <span class="field-cell__label">{{ field.label }}</span>
                  <span class="field-cell__value">{{ field.value }}</span>
                </div>
              </div>
            </div>
            <div class="photo-box">
              <div class="photo-box__inner">
                <img
                  v-if="record.student.picture"
                  :src="imagePreviewUrl(record.student.picture)"
                  alt=""
                  class="photo-box__img"
                />
                <div v-else class="photo-box__placeholder">
                  <feather-icon
                    icon="UserIcon"
                    size="48"
                    class="screen-only-icon"
                  />
                  <span class="print-only-text">{{ $t("Photo") }}</span>
                </div>
              </div>
              <div class="photo-box__caption">{{ $t("Photograph") }}</div>
            </div>
          </div>

          <div class="section-block">
            <h2 class="section-block__title">{{ $t("Location & Branch") }}</h2>
            <div class="fields-grid fields-grid--mixed">
              <div
                v-for="field in record.locationFields"
                :key="'loc-' + field.key"
                class="field-cell"
                :class="fieldCellClass(field)"
              >
                <span class="field-cell__label">{{ field.label }}</span>
                <span class="field-cell__value">{{ field.value }}</span>
              </div>
            </div>
          </div>

          <div class="section-block section-block--academic">
            <h2 class="section-block__title">
              {{ $t("Academic Information") }}
            </h2>
            <div class="fields-grid fields-grid--mixed fields-grid--readable">
              <div
                v-for="field in record.academicFields"
                :key="'acad-' + field.key"
                class="field-cell"
                :class="fieldCellClass(field)"
              >
                <span class="field-cell__label">{{ field.label }}</span>
                <span class="field-cell__value">{{ field.value }}</span>
              </div>
            </div>
          </div>

          <div class="section-block">
            <h2 class="section-block__title">
              {{ $t("Scholarship & Status") }}
            </h2>
            <div class="fields-grid fields-grid--mixed fields-grid--readable">
              <div
                v-for="field in record.statusFields"
                :key="'status-' + field.key"
                class="field-cell"
                :class="fieldCellClass(field)"
              >
                <span class="field-cell__label">{{ field.label }}</span>
                <span class="field-cell__value">{{ field.value }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="print-page print-page--two">
          <div class="page-continuation">
            <span class="page-continuation__title">{{
              $t("STUDENT ADMISSION FORM")
            }}</span>
            <span class="page-continuation__meta">
              {{ val(record.student.name) }}
              <!-- · {{ $t("GR No") }}
              {{ val(record.student.grNo) }} -->
            </span>
          </div>

          <div class="section-block section-block--docs">
            <h2 class="section-block__title">{{ $t("Student Documents") }}</h2>
            <table class="docs-table" v-if="record.documents.length">
              <thead>
                <tr>
                  <th class="col-num">#</th>
                  <th class="col-type">{{ $t("Document Type") }}</th>
                  <th class="col-status">{{ $t("Status") }}</th>
                  <th class="col-file">{{ $t("File") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(doc, di) in record.documents" :key="'doc-' + di">
                  <td class="text-center">{{ di + 1 }}</td>
                  <td>{{ docTitle(doc) }}</td>
                  <td>
                    <span
                      class="doc-status"
                      :class="
                        doc.url ? 'doc-status--uploaded' : 'doc-status--missing'
                      "
                    >
                      {{ docStatusLabel(doc) }}
                    </span>
                  </td>
                  <td class="col-file">
                    <a
                      v-if="doc.url"
                      :href="doc.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="doc-link"
                      :title="doc.url"
                    >
                      {{ docFileLabel(doc) }}
                    </a>
                    <span v-else class="doc-missing">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-else class="docs-empty">
              {{ $t("No document types defined.") }}
            </p>
          </div>

          <div class="section-block section-block--zakat">
            <h2 class="section-block__title">{{ record.zakat.title }}</h2>
            <p class="zakat-print-subtitle">{{ record.zakat.subtitle }}</p>

            <div class="zakat-print-meta">
              <div class="field-cell field-cell--half">
                <span class="field-cell__label">Eligibility</span>
                <span class="field-cell__value">{{
                  record.zakat.eligibilityLabel
                }}</span>
              </div>
            </div>

            <div class="zakat-print-info">{{ record.zakat.infoBox }}</div>

            <ul class="zakat-print-list">
              <li
                v-for="(item, zi) in record.zakat.bullets"
                :key="'zakat-print-' + index + '-' + zi"
                class="zakat-print-item"
              >
                <span class="zakat-print-item__main">{{ item.text }}</span>
                <ul
                  v-if="item.subItems && item.subItems.length"
                  class="zakat-print-sub-list"
                >
                  <li
                    v-for="(subItem, si) in item.subItems"
                    :key="'zakat-print-sub-' + index + '-' + zi + '-' + si"
                  >
                    {{ subItem }}
                  </li>
                </ul>
              </li>
            </ul>

            <div
              class="zakat-print-response"
              :class="'zakat-print-response--' + record.zakat.responseClass"
            >
              {{ record.zakat.responseText }}
            </div>
          </div>

          <footer class="sheet-footer">
            <div class="signature-line">
              <span>{{ $t("Signature of Parents / Guardian") }}</span>
            </div>
            <div class="signature-line">
              <span>{{ $t("Signature of Principal") }}</span>
            </div>
          </footer>
        </div>

        <!-- preview docs -->
        <!-- <div
          v-if="false"
          v-for="(doc, di) in filteredDocs(record.documents)"
          :key="'doc-' + di"
          class="print-page print-page--two"
        >
          <div class="page-continuation">
            <span class="page-continuation__title">{{
              $t("STUDENT ADMISSION FORM")
            }}</span>
            <span class="page-continuation__meta">
              {{ val(record.student.name) }}
            </span>
          </div>

          <div class="section-block section-block--docs">
            <h2 class="section-block__title">{{ docTitle(doc) }}</h2>
            <div class="text-center">
              <img
                :class="`preview-doc-${doc.id}`"
                :src="doc.url"
                :alt="docTitle(doc)"
                style="width: 75%; object-fit: contain"
              />
            </div>
          </div>
        </div> -->
      </div>

      <div v-if="records.length === 0" class="text-center py-5 no-data">
        <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
        <p>{{ $t("No student records found.") }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";
import { mapActions } from "vuex";
import JSZip from "jszip";
import { saveAs as fileSaveAs } from "file-saver";
import {
  parseStudentDocsPayload,
  parseDocTypesPayload,
  resolveTypeWiseDocuments,
} from "@/views/myskool/student/studentDocsUtils";
import { buildZakatPrintSection } from "@/views/myskool/student/zakatEligibilityCopy";
import { getMediaPreviewUrl } from "@/utils/gdriveUpload";

function val(v) {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

function parseStudentDetailResponse(res) {
  if (!res || typeof res !== "object") return null;
  if (res.student && res.studentDetails) return res;
  if (res.data && res.data.student) {
    return {
      student: res.data.student,
      studentDetails: res.data.studentDetails || {},
    };
  }
  return null;
}

function formatPercent(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n % 1 === 0 ? `${n}%` : `${n.toFixed(2)}%`;
}

const PDF_PAGE_WIDTH_MM = 210;
const PDF_PAGE_HEIGHT_MM = 297;
const PDF_PX_PER_MM = 96 / 25.4;
const PDF_A4_WIDTH_PX = 794;
const PDF_A4_HEIGHT_PX = 1122;
const PDF_A4_RATIO = PDF_PAGE_HEIGHT_MM / PDF_PAGE_WIDTH_MM;
const PDF_CANVAS_SCALE = 2;
const PDF_MARGIN_X_MM = 10;
const PDF_MARGIN_Y_MM = 8;
// Rendering on a slightly larger virtual page than strictly needed makes every
// glyph land smaller once the sheet is scaled down onto A4.
const PDF_COMFORT_ZOOM = 1.1;

// Loaded as pre-built UMD bundles at runtime rather than imported: the npm
// builds of jspdf ship class fields and optional chaining that this project's
// webpack cannot parse.
const HTML2CANVAS_SRC =
  "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
const JSPDF_SRC =
  "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";

const CDN_PROXY_PREFIX = "/cdn-proxy";
const UPLOAD_PROXY_PREFIX = "/upload-proxy";

function resolvePdfImageFetchUrl(url) {
  const raw = String(url || "").trim();
  if (!raw || raw.startsWith("data:")) return raw;
  if (raw.startsWith(CDN_PROXY_PREFIX) || raw.startsWith(UPLOAD_PROXY_PREFIX)) {
    return raw;
  }

  try {
    const parsed = new URL(raw, window.location.origin);
    const path = `${parsed.pathname}${parsed.search}`;

    if (/^cdn\.cloudious\.net$/i.test(parsed.hostname)) {
      return `${CDN_PROXY_PREFIX}${path}`;
    }
    if (/^upload\.myskool\.app$/i.test(parsed.hostname)) {
      return `${UPLOAD_PROXY_PREFIX}${path}`;
    }
    if (parsed.origin === window.location.origin) {
      return parsed.href;
    }
  } catch (e) {
    // fall through
  }

  return raw;
}

function sniffImageMime(blob, url) {
  const type = String((blob && blob.type) || "").toLowerCase();
  if (type.startsWith("image/") && type !== "image/svg+xml") return type;

  const path = String(url || "")
    .split("?")[0]
    .split("#")[0];
  const ext = path.includes(".") ? path.split(".").pop().toLowerCase() : "";
  const map = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    bmp: "image/bmp",
    svg: "image/svg+xml",
    avif: "image/avif",
  };
  if (ext && map[ext]) return map[ext];
  if (type.startsWith("image/")) return type;
  return "image/jpeg";
}

function isLikelyHtmlBlob(blob) {
  const type = String((blob && blob.type) || "").toLowerCase();
  return type.includes("text/html") || type.includes("application/json");
}

async function drawBitmapToDataUrl(source, mime) {
  const width = source.width || source.naturalWidth || 1;
  const height = source.height || source.naturalHeight || 1;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, width, height);
  const usePng =
    mime === "image/png" ||
    mime === "image/svg+xml" ||
    String(mime).includes("svg");
  return canvas.toDataURL(usePng ? "image/png" : "image/jpeg", 0.92);
}

async function decodeBlobWithImageBitmap(blob, mime) {
  if (typeof createImageBitmap !== "function") return null;
  const typedBlob =
    blob.type && blob.type !== "application/octet-stream"
      ? blob
      : new Blob([blob], { type: mime });
  const bitmap = await createImageBitmap(typedBlob);
  try {
    return await drawBitmapToDataUrl(bitmap, mime);
  } finally {
    if (typeof bitmap.close === "function") bitmap.close();
  }
}

async function decodeBlobWithImageElement(blob, mime) {
  const objectUrl = URL.createObjectURL(
    blob.type && blob.type !== "application/octet-stream"
      ? blob
      : new Blob([blob], { type: mime })
  );
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("Image element decode failed"));
      el.src = objectUrl;
    });
    return await drawBitmapToDataUrl(img, mime);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function blobToRasterDataUrl(blob, sourceUrl = "") {
  if (!blob || !blob.size || isLikelyHtmlBlob(blob)) return null;

  const mime = sniffImageMime(blob, sourceUrl);

  try {
    const fromBitmap = await decodeBlobWithImageBitmap(blob, mime);
    if (fromBitmap) return fromBitmap;
  } catch (e) {
    // try next strategy
  }

  try {
    const fromImage = await decodeBlobWithImageElement(blob, mime);
    if (fromImage) return fromImage;
  } catch (e) {
    // try next strategy
  }

  // Last resort: if the blob is already a supported raster type, pass through.
  if (
    mime === "image/jpeg" ||
    mime === "image/png" ||
    mime === "image/gif" ||
    mime === "image/webp"
  ) {
    try {
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("FileReader failed"));
        reader.readAsDataURL(
          blob.type && blob.type !== "application/octet-stream"
            ? blob
            : new Blob([blob], { type: mime })
        );
      });
    } catch (e) {
      // fall through
    }
  }

  return null;
}

export default {
  name: "StudentTcfPrint",
  components: { BButton, BSpinner, BBadge },
  data() {
    return {
      loading: false,
      downloading: false,
      error: null,
      records: [],
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: "",
    };
  },
  computed: {
    currentDate() {
      return new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    ...mapActions({ get: "get" }),

    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || this.$store.state.userData.cId;

      if (!this.db) {
        this.error = this.$t(
          "Database identifier is missing. Please close and try again."
        );
        return;
      }

      // if (!query.sID) {
      //   this.error = this.$t("Student ID is missing.");
      //   return;
      // }

      // this.loadCampusInfo();
      this.loadAll();
    },

    async loadCampusInfo() {
      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/campuses",
          {
            params: { db: this.db, cId: this.cID },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch = match.branch || "";
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        console.warn("Campus info fetch failed");
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => b.id == this.cID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.name ||
            currentBranch.branch ||
            this.campusName;
          this.campusBranch = currentBranch.branch || "";
          this.schoolLogo = currentBranch.logo || this.schoolLogo;
        }
      }
    },

    val(v) {
      return val(v);
    },

    imagePreviewUrl(url) {
      if (!url) return "";
      return getMediaPreviewUrl(url, "Image");
    },

    fieldCellClass(field) {
      const size = field.size || (field.full ? "full" : "sm");
      return `field-cell--${size}`;
    },

    formatDate(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return String(dateStr);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    formatClass(metaVal) {
      const metaClass = String(metaVal || "").trim();
      if (metaClass.includes(" - ")) {
        const parts = metaClass.split(" - ");
        let className = (parts[0] || "").trim();
        let sectionName = "";
        if (parts.length > 1) {
          sectionName = parts.slice(1).join(" - ").trim();
        }
        return sectionName ? sectionName : className;
      } else return metaClass;
    },

    buildRecordFields(student, details) {
      const s = student || {};
      const d = details || {};

      return {
        basicFields: [
          {
            key: "name",
            label: this.$t("Student Name"),
            value: val(s.name),
            size: "lg",
          },
          {
            key: "father_name",
            label: this.$t("Father Name"),
            value: val(s.father_name),
            size: "lg",
          },
          {
            key: "gender",
            label: this.$t("Gender"),
            value: val(s.gender),
            size: "sm",
          },
          {
            key: "class",
            label: this.$t("Class"),
            value: val(s.meta_class || s.class_name),
            size: "lg",
          },
          {
            key: "contact_student",
            label: this.$t("Primary Contact"),
            value: val(s.contact_student),
            size: "sm",
          },
          {
            key: "contact_father",
            label: this.$t("WhatsApp Number"),
            value: val(s.contact_father),
            size: "sm",
          },
          {
            key: "contact_mother",
            label: this.$t("Parent's Contact"),
            value: val(s.contact_mother),
            size: "sm",
          },
          {
            key: "studentCnic",
            label: this.$t("Student's CNIC No."),
            value: val(d.studentCnic),
            size: "full",
          },
        ],
        locationFields: [
          {
            key: "region",
            label: this.$t("Region"),
            value: val(d.region),
            size: "sm",
          },
          {
            key: "area",
            label: this.$t("Area"),
            value: val(d.area),
            size: "sm",
          },
          {
            key: "branchCode",
            label: this.$t("Branch Code"),
            value: val(d.branchCode),
            size: "sm",
          },
          {
            key: "school",
            label: this.$t("School"),
            value: val(d.school),
            size: "lg",
          },
          {
            key: "instituteName",
            label: this.$t("Institute Name"),
            value: val(d.instituteName),
            size: "lg",
          },
        ],
        academicFields: [
          {
            key: "federalRegNo",
            label: this.$t("Federal Board Registration No"),
            value: val(d.federalRegNo),
            size: "lg",
          },
          {
            key: "matricRollNo",
            label: this.$t("Matric Roll No."),
            value: val(d.matricRollNo),
            size: "sm",
          },
          {
            key: "matricBoard",
            label: this.$t("Matric Board"),
            value: val(d.matricBoard),
            size: "sm",
          },
          {
            key: "matricPerc",
            label: this.$t("Matric %"),
            value: formatPercent(d.matricPerc),
            size: "sm",
          },
          {
            key: "matricGrade",
            label: this.$t("Matric Grade"),
            value: val(d.matricGrade),
            size: "sm",
          },
          {
            key: "ninthPerc",
            label: this.$t("9th %"),
            value: formatPercent(d.ninthPerc),
            size: "sm",
          },
          {
            key: "hsstPerc",
            label: this.$t("HSST %"),
            value: formatPercent(d.hsstPerc),
            size: "sm",
          },
          {
            key: "overallPerc",
            label: this.$t("Overall Score (HSST + Matric)"),
            value: formatPercent(d.overallPerc),
            size: "lg",
          },
          {
            key: "hsstStatus",
            label: this.$t("HSST Status"),
            value: val(d.hsstStatus),
            size: "sm",
          },
          {
            key: "interFaculty",
            label: this.$t("Inter Faculty"),
            value: val(this.formatClass(s.meta_class)),
            size: "sm",
          },
          {
            key: "intermediateBoard",
            label: this.$t("Intermediate Board"),
            value: val(d.intermediateBoard),
            size: "sm",
          },
          {
            key: "firstYearRollNo",
            label: this.$t("1st Year Roll Number"),
            value: val(d.firstYearRollNo),
            size: "sm",
          },
          {
            key: "firstYearPerc",
            label: this.$t("1st Year %"),
            value: formatPercent(d.firstYearPerc),
            size: "sm",
          },
          {
            key: "paperPassed",
            label: this.$t("1st Year Paper Passed"),
            value: val(d.paperPassed),
            size: "sm",
          },
          {
            key: "secondYearRollNo",
            label: this.$t("Second Year Roll No"),
            value: val(d.secondYearRollNo),
            size: "sm",
          },
          {
            key: "secondYearPerc",
            label: this.$t("Second Year %"),
            value: formatPercent(d.secondYearPerc),
            size: "sm",
          },
        ],
        statusFields: [
          {
            key: "scholarshipCategory",
            label: this.$t("Scholarship Category"),
            value: val(d.scholarshipCategory),
            size: "lg",
          },
          {
            key: "scholarshipStatus",
            label: this.$t("Scholarship Confirmation"),
            value: val(d.scholarshipStatus),
            size: "sm",
          },
          {
            key: "studentStatus",
            label: this.$t("Status"),
            value: val(d.studentStatus),
            size: "sm",
          },
          {
            key: "comment",
            label: this.$t("Comment"),
            value: val(d.comment),
            size: "full",
          },
        ],
      };
    },

    async loadDocTypes(campusID) {
      const res = await this.get({
        url:
          this.$store.state.domain +
          "DocumentTypes/LoadData?db=" +
          this.db +
          "&cID=" +
          campusID +
          "&type=student",
        token: this.$store.state.userData.token,
      });
      return parseDocTypesPayload(res);
    },

    async loadDocuments(studentID, campusID, className) {
      const res = await this.get({
        url:
          this.$store.state.domain +
          "DocumentDetails/LoadStudentDocs?db=" +
          this.db +
          "&cID=" +
          campusID +
          "&studentID=" +
          studentID,
        token: this.$store.state.userData.token,
        returnData: true,
      });

      let { templates, uploaded } = parseStudentDocsPayload(res);
      return [...templates, ...uploaded];
      // const docTypes = await this.loadDocTypes(campusID);

      // return resolveTypeWiseDocuments(
      //   templates,
      //   uploaded,
      //   docTypes,
      //   className
      // );
    },

    async loadStudentRecord(sID) {
      const res = await this.get({
        url:
          this.$store.state.domain +
          "StudentDetails/GetByStudentID?db=" +
          this.db +
          "&sID=" +
          sID,
        token: this.$store.state.userData.token,
      });

      const parsed = parseStudentDetailResponse(res);
      if (!parsed || !parsed.student) return null;

      const student = parsed.student;
      const details = parsed.studentDetails || {};
      const campusID = student.campusID || details.campusID || this.cID;
      const className = student.meta_class || student.class_name || "";
      let documents = [];
      try {
        documents = await this.loadDocuments(sID, campusID, className);
      } catch (docErr) {
        console.warn("StudentTcfPrint docs load failed:", docErr);
      }
      const fields = this.buildRecordFields(student, details);

      return {
        student,
        studentDetails: details,
        documents,
        zakat: buildZakatPrintSection(details),
        ...fields,
      };
    },

    async loadFiltered(sID) {
      const res = await this.get({
        url:
          this.$store.state.domain +
          "StudentDetails/Filter?db=" +
          this.db +
          "&campusID=" +
          this.cID +
          "&sIDs=" +
          sID,
        token: this.$store.state.userData.token,
      });
      return res;
    },
    mapStudentRecords(items) {
      const mappedRecords = items.map((el) => {
        const fields = this.buildRecordFields(el.student, el.studentDetails);
        const zakat = buildZakatPrintSection(el.studentDetails);
        return {
          campus: el.campus,
          student: el.student,
          studentDetails: el.studentDetails,
          documents: el.documentDetails,
          zakat,
          ...fields,
        };
      });
      return mappedRecords;
    },
    async loadAll() {
      this.loading = true;
      this.error = null;
      this.records = [];

      try {
        const ids = String(this.$route.query.sID || "")
          .split(",")
          .map((id) => parseInt(id.trim(), 10))
          .filter((id) => id > 0);

        if (this.$route.query.sID && !ids.length) {
          this.error = this.$t("No valid student IDs provided.");
          return;
        }

        const resp = await this.loadFiltered(ids);

        const records = Array.isArray(resp) ? resp : [];
        this.records = this.mapStudentRecords(records);
        console.log(this.records);

        await this.$nextTick();
        // for (const id of ids) {
        //   const record = await this.loadStudentRecord(id);
        //   if (record) this.records.push(record);
        // }

        if (!this.records.length) {
          this.error = this.$t("Could not load student data.");
        }
      } catch (e) {
        console.error("StudentTcfPrint load error:", e);
        this.error = this.$t("Failed to load student records.");
      } finally {
        this.loading = false;
      }
    },

    isImageUrl(url) {
      if (!url || typeof url !== "string") return false;
      const imgExt = /\.(jpeg|jpg|png|gif|bmp|webp|svg)(\?.*)?$/i;
      return imgExt.test(url);
    },
    filteredDocs(docs) {
      if (Array.isArray(docs) && docs.length > 0) {
        return docs.filter(
          (el) =>
            el.status == "approved" &&
            el.url &&
            this.isImageUrl(el.url) &&
            el.url.includes("cdn.cloudious.net")
        );
      }
      return [];
    },

    docTitle(doc) {
      return doc.meta_title || doc.title || this.$t("Document");
    },

    docStatusLabel(doc) {
      const status = (doc.status || "").trim();
      if (status) return status;
      return doc.url ? this.$t("Uploaded") : this.$t("Not Uploaded");
    },

    docFileLabel(doc) {
      if (!doc.url) return "—";
      const raw = String(doc.url).split("?")[0];
      const parts = raw.split("/");
      const name = decodeURIComponent(parts[parts.length - 1] || "").trim();
      if (name && name.length > 3 && name !== "undefined") {
        return name.length > 42 ? `${name.slice(0, 39)}…` : name;
      }
      return this.$t("View Document");
    },

    print() {
      window.print();
    },

    safeDownloadName(value, fallback) {
      const raw = String(value || fallback || "student")
        .replace(/[\\/:*?"<>|]/g, "-")
        .replace(/\s+/g, " ")
        .trim();
      return raw || String(fallback || "student");
    },

    buildPdfFilename(record, index) {
      const student = (record && record.student) || {};
      const details = (record && record.studentDetails) || {};
      const federalRegNo = String(details.federalRegNo || "").trim();
      const studentName = String(student.name || "").trim();

      const safeReg = federalRegNo ? this.safeDownloadName(federalRegNo) : "";
      const safeName = studentName ? this.safeDownloadName(studentName) : "";

      if (safeReg && safeName) {
        return `${safeReg}_${safeName}.pdf`;
      }
      if (safeReg) {
        return `${safeReg}.pdf`;
      }
      if (safeName) {
        return `${safeName}.pdf`;
      }
      return `student-${index + 1}.pdf`;
    },

    triggerBlobDownload(blob, filename) {
      if (typeof fileSaveAs === "function") {
        fileSaveAs(blob, filename);
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    },

    loadScriptOnce(src) {
      return new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[data-src="${src}"]`);
        if (existing) {
          if (existing.getAttribute("data-loaded") === "1") {
            resolve();
            return;
          }
          existing.addEventListener("load", () => resolve(), { once: true });
          existing.addEventListener(
            "error",
            () => reject(new Error(`Failed to load ${src}`)),
            { once: true }
          );
          return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.setAttribute("data-src", src);
        script.onload = () => {
          script.setAttribute("data-loaded", "1");
          resolve();
        };
        script.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(script);
      });
    },

    async ensurePdfLibs() {
      await Promise.all([
        typeof window.html2canvas === "function"
          ? Promise.resolve()
          : this.loadScriptOnce(HTML2CANVAS_SRC),
        window.jspdf && window.jspdf.jsPDF
          ? Promise.resolve()
          : this.loadScriptOnce(JSPDF_SRC),
      ]);

      const html2canvas = window.html2canvas;
      const jsPDF = window.jspdf && window.jspdf.jsPDF;
      if (typeof html2canvas !== "function" || typeof jsPDF !== "function") {
        throw new Error("PDF libraries are not available");
      }
      return { html2canvas, jsPDF };
    },

    // Each .print-page is rasterised on its own and drawn edge to edge on one A4
    // sheet. Going straight to html2canvas/jsPDF instead of html2pdf avoids its
    // wrapper container, which cropped the right edge of the sheet, and makes the
    // page count exactly the number of .print-page elements.
    async buildAdmissionPdfBlob(exportRoot) {
      const pages = Array.from(exportRoot.querySelectorAll(".print-page"));
      if (!pages.length) return null;

      const { html2canvas, jsPDF } = await this.ensurePdfLibs();

      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        compress: true,
      });

      for (let i = 0; i < pages.length; i += 1) {
        const page = pages[i];
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(page, {
          scale: PDF_CANVAS_SCALE,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          // Lay the clone out at the sheet's own width so a viewport narrower
          // than the virtual page can never crop it.
          windowWidth: page.offsetWidth,
          windowHeight: page.offsetHeight,
        });

        if (i > 0) pdf.addPage();
        pdf.addImage(
          canvas.toDataURL("image/jpeg", 0.98),
          "JPEG",
          0,
          0,
          PDF_PAGE_WIDTH_MM,
          PDF_PAGE_HEIGHT_MM,
          undefined,
          "FAST"
        );
      }

      return pdf.output("blob");
    },

    copyScopedAttrs(target) {
      if (!this.$el || !target) return;
      Array.from(this.$el.attributes).forEach((attr) => {
        if (attr.name.startsWith("data-v-")) {
          target.setAttribute(attr.name, attr.value);
        }
      });
    },

    createPdfExportHost() {
      const host = document.createElement("div");
      host.className = "pdf-export-host";
      host.setAttribute("aria-hidden", "true");
      // Parked off-screen rather than hidden: html2canvas now rasterises these
      // nodes in place, and it skips anything with visibility:hidden.
      host.style.cssText = [
        "position:fixed",
        "left:-10000px",
        "top:0",
        `width:${PDF_A4_WIDTH_PX}px`,
        "pointer-events:none",
        "z-index:-1",
        "overflow:visible",
      ].join(";");
      document.body.appendChild(host);

      return host;
    },

    buildPdfExportRoot(host) {
      const root = document.createElement("div");
      root.className = "student-tcf-print is-pdf-export";
      this.copyScopedAttrs(root);
      host.appendChild(root);
      return root;
    },

    cloneSheetForPdfExport(sheet) {
      const clone = sheet.cloneNode(true);
      this.swapScreenOnlyIcons(clone);
      return clone;
    },

    // html2canvas can still paint an SVG that is only display:none'd, dropping a
    // stray glyph elsewhere on the page, so the icons are removed outright.
    swapScreenOnlyIcons(scope) {
      scope.querySelectorAll(".screen-only-icon").forEach((el) => {
        el.remove();
      });
      scope.querySelectorAll(".print-only-text").forEach((el) => {
        el.style.display = "block";
      });
    },

    setImgSrcAndWait(img, src) {
      return new Promise((resolve) => {
        const finish = () => resolve();
        if (
          img.getAttribute("src") === src &&
          img.complete &&
          img.naturalWidth > 0
        ) {
          finish();
          return;
        }
        img.onload = finish;
        img.onerror = finish;
        img.setAttribute("src", src);
        if (img.complete) finish();
      });
    },

    // Fetches the URL returned by the student/campus APIs through a same-origin
    // proxy, rasterises the blob, and returns a base64 data URL for html2canvas.
    async fetchImageBase64FromApiUrl(apiUrl) {
      const raw = String(apiUrl || "").trim();
      if (!raw) return null;
      if (raw.startsWith("data:")) return raw;

      const normalized = getMediaPreviewUrl(raw, "Image");
      const fetchUrl = resolvePdfImageFetchUrl(normalized);

      try {
        const res = await axios.get(fetchUrl, {
          responseType: "blob",
          timeout: 30000,
        });
        const blob = res.data;
        if (!blob || !blob.size || isLikelyHtmlBlob(blob)) {
          console.warn("PDF image fetch returned non-image blob:", normalized);
          return null;
        }
        const dataUrl = await blobToRasterDataUrl(blob, normalized);
        if (dataUrl) return dataUrl;
        console.warn("PDF image decode failed:", normalized);
      } catch (e) {
        console.warn("PDF image fetch failed:", normalized, e);
      }

      return null;
    },

    async applyBase64Image(exportRoot, selector, dataUrl) {
      const img = exportRoot.querySelector(selector);
      if (!img) return;
      if (!dataUrl) {
        img.removeAttribute("src");
        img.style.display = "none";
        return;
      }
      await this.setImgSrcAndWait(img, dataUrl);
    },

    // Logo and photo URLs come from the campus + student APIs. They are fetched
    // as blobs (via /cdn-proxy), converted to base64, then injected into the
    // export DOM so html2canvas can paint them onto the PDF.
    async prepareImagesForPdfExport(exportRoot, { logoUrl, photoUrl }) {
      const [logoDataUrl, photoDataUrl] = await Promise.all([
        this.fetchImageBase64FromApiUrl(logoUrl),
        this.fetchImageBase64FromApiUrl(photoUrl),
      ]);

      await Promise.all([
        this.applyBase64Image(exportRoot, ".letterhead__logo-img", logoDataUrl),
        this.applyBase64Image(exportRoot, ".photo-box__img", photoDataUrl),
      ]);
    },

    removePdfExportHost(host) {
      if (host && host.parentNode) {
        host.parentNode.removeChild(host);
      }
    },

    // Sizes every .print-page to one A4 sheet without restyling the design. The
    // virtual page is rendered larger than A4 and scaled back down when drawn,
    // which is a uniform zoom-out: the margins scale with it, so they stay
    // exactly PDF_MARGIN_*_MM on paper and stay equal left to right.
    fitPagesToA4(host, root) {
      const pages = Array.from(root.querySelectorAll(".print-page"));
      if (!pages.length) return;

      root.style.removeProperty("width");
      root.style.removeProperty("max-width");
      host.style.width = `${PDF_A4_WIDTH_PX}px`;
      pages.forEach((page) => {
        page.style.removeProperty("width");
        page.style.removeProperty("padding");
        page.style.setProperty("height", "auto", "important");
      });

      const marginX = PDF_MARGIN_X_MM * PDF_PX_PER_MM;
      const marginY = PDF_MARGIN_Y_MM * PDF_PX_PER_MM;
      const boxWidth = PDF_A4_WIDTH_PX - marginX * 2;
      const boxHeight = PDF_A4_HEIGHT_PX - marginY * 2;

      const content = pages.reduce(
        (max, page) => ({
          width: Math.max(max.width, page.scrollWidth - marginX * 2),
          height: Math.max(max.height, page.scrollHeight - marginY * 2),
        }),
        { width: 0, height: 0 }
      );

      const factor =
        Math.max(1, content.height / boxHeight, content.width / boxWidth) *
        PDF_COMFORT_ZOOM;

      const width = Math.round(PDF_A4_WIDTH_PX * factor);
      const height = Math.round(width * PDF_A4_RATIO);
      const padX = Math.round(marginX * factor);
      const padY = Math.round(marginY * factor);

      host.style.width = `${width}px`;
      root.style.setProperty("width", `${width}px`, "important");
      root.style.setProperty("max-width", `${width}px`, "important");
      pages.forEach((page) => {
        page.style.setProperty("width", `${width}px`, "important");
        page.style.setProperty("height", `${height}px`, "important");
        page.style.setProperty("padding", `${padY}px ${padX}px`, "important");
      });
    },

    waitForPaint() {
      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      });
    },

    async downloadPdf() {
      if (this.downloading) return;
      if (!this.records.length) {
        if (this.$bvToast) {
          this.$bvToast.toast(this.$t("No student records found."), {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
        }
        return;
      }

      await this.$nextTick();

      const screenStack = this.$el.querySelector(".print-stack");
      const sheets = screenStack
        ? Array.from(screenStack.querySelectorAll(".admission-sheet"))
        : [];

      if (!sheets.length) {
        if (this.$bvToast) {
          this.$bvToast.toast(this.$t("No student records found."), {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
        }
        return;
      }

      this.downloading = true;
      let exportHost = null;

      try {
        await this.ensurePdfLibs();
        exportHost = this.createPdfExportHost();
        const exportRoot = this.buildPdfExportRoot(exportHost);

        const pdfs = [];

        for (let i = 0; i < sheets.length; i += 1) {
          exportRoot.innerHTML = "";
          const stack = document.createElement("div");
          stack.className = "print-stack";
          this.copyScopedAttrs(stack);
          stack.appendChild(this.cloneSheetForPdfExport(sheets[i]));
          exportRoot.appendChild(stack);

          await this.waitForPaint();
          this.fitPagesToA4(exportHost, exportRoot);
          await this.waitForPaint();

          // const stdDocs = this.filteredDocs(this.records[i].documents);
          // console.log(stdDocs);

          // eslint-disable-next-line no-await-in-loop
          await this.prepareImagesForPdfExport(exportRoot, {
            logoUrl: this.records[i].campus.logo,
            photoUrl: this.records[i].student.picture,
          });
          await this.waitForPaint();

          const filename = this.buildPdfFilename(this.records[i], i);
          // eslint-disable-next-line no-await-in-loop
          const blob = await this.buildAdmissionPdfBlob(exportRoot);
          if (!blob) {
            throw new Error(`Failed to create PDF for ${filename}`);
          }
          pdfs.push({ filename, blob });
        }

        if (pdfs.length === 1) {
          this.triggerBlobDownload(pdfs[0].blob, pdfs[0].filename);
        } else {
          const zip = new JSZip();
          pdfs.forEach((pdf) => {
            zip.file(pdf.filename, pdf.blob);
          });
          const zipBlob = await zip.generateAsync({ type: "blob" });
          this.triggerBlobDownload(zipBlob, "student-admission-forms.zip");
        }
      } catch (e) {
        console.error("Admission form download failed", e);
        const message =
          e && String(e.message || "").includes("Image")
            ? this.$t(
                "Failed to download admission form. One or more images could not be loaded — the PDF may still work if you retry, or use Print instead."
              )
            : this.$t("Failed to download admission form.");
        if (this.$bvToast) {
          this.$bvToast.toast(message, {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
        }
      } finally {
        this.removePdfExportHost(exportHost);
        this.downloading = false;
      }
    },

    close() {
      if (window.history.length > 1) {
        this.$router.go(-1);
        return;
      }
      this.$router.push({ name: "manage-studentgrid" });
    },
  },
};
</script>

<style lang="scss" scoped>
.student-tcf-print {
  background: #e8eaed;
  min-height: 100vh;
  padding: 1.5rem;
  font-family: "Segoe UI", "Helvetica Neue", Arial, sans-serif;
  color: #1a1a1a;
  -webkit-font-smoothing: antialiased;
}

.control-bar {
  background: #fff;
  padding: 1rem 1.5rem;
  margin-bottom: 1.25rem;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0.75rem;
  z-index: 1000;
  font-family: "Inter", "Segoe UI", sans-serif;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn-modern {
  display: inline-flex;
  align-items: center;
  border: none;
  border-radius: 8px;
  padding: 0.55rem 1.1rem;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
}

.btn-print {
  background: #7367f0;
  color: #fff;
}

.btn-print:disabled {
  opacity: 0.65;
}

.btn-close {
  background: #fff;
  color: #444;
  border: 1px solid #d1d5db;
}

.print-stack {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 210mm;
  width: 100%;
  margin: 0 auto;
}

.admission-sheet {
  background: #fff;
  border: 1px solid #bbb;
  padding: 0;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.print-page {
  padding: 1.5rem 1.85rem 1.35rem;
}

.print-page--two {
  border-top: 2px dashed #d1d5db;
  display: flex;
  flex-direction: column;
}

.page-continuation {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.65rem;
  border-bottom: 2px solid #111;
}

.page-continuation__title {
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #111;
}

.page-continuation__meta {
  font-size: 0.92rem;
  font-weight: 600;
  color: #374151;
}

.letterhead {
  margin-bottom: 0.55rem;
}

.letterhead__row {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.45rem;
}

.letterhead__logo-box {
  flex-shrink: 0;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  border: 2px solid #222;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
}

.letterhead__logo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.letterhead__logo-placeholder {
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
}

.letterhead__school {
  flex: 1;
  text-align: center;
  padding-right: 72px;
}

.letterhead__school-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  line-height: 1.3;
}

.letterhead__school-branch {
  margin: 0.15rem 0 0;
  font-size: 0.82rem;
  color: #444;
}

.letterhead__title {
  margin: 0;
  text-align: center;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.letterhead__divider {
  margin-top: 0.55rem;
  border-bottom: 2px solid #111;
}

.meta-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.15rem 2rem;
  margin: 0.75rem 0 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #ddd;
}

.field-cell {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
}

.field-cell--half {
  min-width: 0;
}

.field-cell--sm {
  grid-column: span 2;
}

.field-cell--lg {
  grid-column: span 3;
}

.field-cell--full {
  grid-column: 1 / -1;
}

.field-cell__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  line-height: 1.35;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.field-cell__value {
  display: block;
  width: 100%;
  box-sizing: border-box;
  font-size: 1rem;
  line-height: 1.5;
  color: #111827;
  border-bottom: 1.5px solid #374151;
  padding: 0.15rem 0 0.55rem;
  min-height: 1.85rem;
  word-break: break-word;
  font-weight: 500;
}

.print-only-text {
  display: none;
}

.form-block {
  display: grid;
  grid-template-columns: 1fr 118px;
  gap: 1rem;
  align-items: start;
  margin-bottom: 0.15rem;
}

.form-block__content {
  min-width: 0;
}

.photo-box {
  flex-shrink: 0;
  width: 118px;
  border: 1.5px solid #111;
  background: #fafafa;
}

.photo-box__inner {
  height: 128px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.photo-box__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-box__placeholder {
  color: #888;
}

.photo-box__caption {
  border-top: 1px solid #111;
  text-align: center;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 3px 4px;
  background: #fff;
}

.fields-grid {
  display: grid;
  gap: 0.95rem 1.35rem;
}

.fields-grid--mixed {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

.fields-grid--readable {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.fields-grid--readable .field-cell--sm {
  grid-column: span 2;
}

.fields-grid--readable .field-cell--lg {
  grid-column: span 2;
}

.fields-grid--readable .field-cell--full {
  grid-column: 1 / -1;
}

.section-block {
  margin-top: 1.15rem;
  padding-top: 0.85rem;
  border-top: 1px solid #bbb;
}

.section-block--academic {
  margin-top: 1.25rem;
}

.section-block--docs {
  margin-top: 1rem;
}

.section-block__title {
  margin: 0 0 0.85rem;
  font-size: 0.92rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #111827;
}

.docs-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.92rem;
  font-family: inherit;
}

.docs-table th,
.docs-table td {
  border: 1px solid #777;
  padding: 0.5rem 0.65rem;
  vertical-align: middle;
  word-wrap: break-word;
  line-height: 1.45;
}

.docs-table th {
  background: #ececec;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.col-num {
  width: 6%;
}

.col-type {
  width: 32%;
}

.col-status {
  width: 22%;
}

.col-file {
  width: 40%;
}

.doc-status {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.doc-status--uploaded {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.doc-status--missing {
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #d1d5db;
}

.doc-link {
  color: #1d4ed8;
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
  word-break: break-all;

  &:hover {
    color: #1e40af;
  }
}

.doc-missing {
  color: #9ca3af;
}

.docs-empty {
  font-size: 0.88rem;
  color: #666;
  margin: 0;
  font-style: italic;
}

.section-block--zakat {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.zakat-print-subtitle {
  margin: 0 0 0.75rem;
  font-size: 0.92rem;
  color: #374151;
  line-height: 1.5;
}

.zakat-print-meta {
  margin-bottom: 0.75rem;
  max-width: 320px;
}

.zakat-print-info {
  padding: 0.75rem 0.85rem;
  margin-bottom: 0.75rem;
  border: 1px solid #cfc6f5;
  background: #f7f5ff;
  font-size: 0.88rem;
  line-height: 1.55;
  color: #1f2937;
}

.zakat-print-list {
  margin: 0 0 0.75rem;
  padding-left: 1.15rem;
  font-size: 0.88rem;
  line-height: 1.55;
}

.zakat-print-item {
  margin-bottom: 0.55rem;
}

.zakat-print-item__main {
  font-weight: 600;
}

.zakat-print-sub-list {
  margin: 0.15rem 0 0;
  padding-left: 1rem;
  list-style-type: circle;
}

.zakat-print-response {
  padding: 0.65rem 0.85rem;
  border: 1px solid #bbb;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.55;
}

.zakat-print-response--yes {
  border-color: #86efac;
  background: #ecfdf5;
  color: #047857;
}

.zakat-print-response--no {
  border-color: #fca5a5;
  background: #fef2f2;
  color: #b91c1c;
}

.zakat-print-response--na {
  border-style: dashed;
  color: #666;
  font-weight: 500;
}

.sheet-footer {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.5rem;
  margin-top: auto;
  padding-top: 3rem;
}

.signature-line {
  border-top: 1.5px solid #111;
  padding-top: 0.55rem;
  font-size: 0.85rem;
  font-weight: 600;
  text-align: center;
}

@mixin admission-print-layout {
  .screen-only-icon {
    display: none !important;
  }

  .print-only-text {
    display: block !important;
  }

  .print-stack {
    max-width: 100% !important;
    width: 100% !important;
    gap: 0 !important;
    margin: 0 !important;
  }

  .admission-sheet {
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    min-height: 0 !important;
    gap: 0 !important;
    page-break-inside: auto;
  }

  .admission-sheet:not(:last-child) {
    page-break-after: always;
  }

  .admission-sheet:last-child {
    page-break-after: avoid !important;
  }

  .print-page {
    padding: 0 !important;
    font-family: "Segoe UI", "Helvetica Neue", Arial, sans-serif !important;
  }

  .print-page--two {
    border-top: none !important;
    display: flex !important;
    flex-direction: column !important;
    padding-top: 0 !important;
    page-break-before: always !important;
    break-before: page !important;
  }

  .page-continuation {
    margin-bottom: 0.75rem !important;
    padding-bottom: 0.55rem !important;
  }

  .page-continuation__title {
    font-size: 9pt !important;
  }

  .page-continuation__meta {
    font-size: 10pt !important;
  }

  .letterhead {
    margin-bottom: 0.5rem !important;
  }

  .letterhead__row {
    margin-bottom: 0.35rem !important;
  }

  .letterhead__logo-box {
    width: 68px !important;
    height: 68px !important;
  }

  .letterhead__school {
    padding-right: 68px !important;
  }

  .letterhead__school-name {
    font-size: 11pt !important;
  }

  .letterhead__title {
    font-size: 13pt !important;
  }

  .letterhead__divider {
    margin-top: 0.4rem !important;
  }

  .meta-row {
    grid-template-columns: 1fr 1fr !important;
    gap: 0.85rem 1.75rem !important;
    margin: 0.55rem 0 0.75rem !important;
    padding-bottom: 0.55rem !important;
  }

  .form-block {
    display: grid !important;
    grid-template-columns: 1fr 118px !important;
    gap: 1rem !important;
    margin-bottom: 0.35rem !important;
  }

  .photo-box {
    width: 118px !important;
  }

  .photo-box__inner {
    height: 132px !important;
  }

  .fields-grid--mixed {
    grid-template-columns: repeat(6, minmax(0, 1fr)) !important;
    gap: 0.65rem 1rem !important;
  }

  .fields-grid--readable {
    grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
    gap: 0.7rem 1.1rem !important;
  }

  .fields-grid--readable .field-cell--sm {
    grid-column: span 2 !important;
  }

  .fields-grid--readable .field-cell--lg {
    grid-column: span 2 !important;
  }

  .fields-grid--readable .field-cell--full {
    grid-column: 1 / -1 !important;
  }

  .field-cell--sm {
    grid-column: span 2 !important;
  }

  .field-cell--lg {
    grid-column: span 3 !important;
  }

  .field-cell--full {
    grid-column: 1 / -1 !important;
  }

  .field-cell__label {
    font-size: 8.5pt !important;
    line-height: 1.35 !important;
  }

  .field-cell__value {
    font-size: 11pt !important;
    line-height: 1.5 !important;
    min-height: 1.65rem !important;
    padding-bottom: 0.45rem !important;
  }

  .section-block {
    margin-top: 0.75rem !important;
    padding-top: 0.55rem !important;
  }

  .section-block--academic {
    margin-top: 0.85rem !important;
  }

  .section-block--docs {
    margin-top: 0 !important;
    padding-top: 0 !important;
    border-top: none !important;
  }

  .section-block__title {
    font-size: 10pt !important;
    margin-bottom: 0.65rem !important;
  }

  .docs-table {
    width: 100% !important;
    font-size: 10pt !important;
  }

  .docs-table th,
  .docs-table td {
    padding: 0.45rem 0.55rem !important;
    line-height: 1.45 !important;
  }

  .docs-table th {
    background: #ececec !important;
    font-size: 9pt !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .zakat-print-subtitle {
    font-size: 10pt !important;
  }

  .zakat-print-info {
    font-size: 9.5pt !important;
    line-height: 1.55 !important;
  }

  .zakat-print-list {
    font-size: 9.5pt !important;
    line-height: 1.55 !important;
  }

  .zakat-print-response {
    font-size: 10pt !important;
    line-height: 1.55 !important;
  }

  .zakat-print-response--yes,
  .zakat-print-response--no {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .doc-status {
    font-size: 8.5pt !important;
  }

  .doc-status--uploaded,
  .doc-status--missing {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .doc-link {
    color: #1155cc !important;
    text-decoration: underline !important;
    font-size: 9pt !important;
  }

  .sheet-footer {
    margin-top: auto !important;
    gap: 2.5rem !important;
    padding-top: 2.75rem !important;
  }

  .signature-line {
    font-size: 10pt !important;
    padding-top: 0.65rem !important;
  }
}

.student-tcf-print.is-pdf-export {
  background: #fff !important;
  padding: 0 !important;
  min-height: 0 !important;
  height: auto !important;
  width: 794px !important;
  max-width: 794px !important;
  margin: 0 auto !important;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;

  @include admission-print-layout;

  .print-stack,
  .admission-sheet {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 auto !important;
  }

  // A4 at 96dpi is 794 x 1122.5px. Each .print-page is one full sheet and
  // carries its own margins as padding, since it gets drawn edge to edge on the
  // paper. fitPagesToA4() overrides the width, height and padding at export
  // time, scaling all three together when the content needs a larger page.
  .print-page {
    box-sizing: border-box !important;
    width: 794px !important;
    height: 1122px !important;
    overflow: hidden !important;
    padding: 8mm 10mm !important;
  }

  .print-page--two {
    display: flex !important;
    flex-direction: column !important;
    padding-top: 8mm !important;
  }

  // The photo column normally ends flush with the content edge, so its 1.5px
  // border lands on the last canvas pixel column and gets shaved off when the
  // sheet is rasterised. minmax(0, ...) also stops the fields column from ever
  // pushing the photo out of the page.
  .form-block {
    grid-template-columns: minmax(0, 1fr) 122px !important;
  }

  .photo-box {
    justify-self: start !important;
  }
}

@media print {
  @page {
    size: A4 portrait;
    margin: 8mm 10mm;
  }

  .no-print {
    display: none !important;
  }

  .student-tcf-print {
    background: #fff !important;
    padding: 0 !important;
    min-height: 0 !important;
    height: auto !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    @include admission-print-layout;

    // A4 minus the @page margins is 281mm; stay just under it so the filled
    // page never spills a sliver, and .sheet-footer still anchors to the bottom
    // exactly like it does in the PDF export.
    .print-page {
      min-height: 280mm !important;
    }
  }
}

@media screen and (max-width: 768px) {
  .letterhead__school {
    padding-right: 0;
    text-align: left;
  }

  .form-block {
    grid-template-columns: 1fr;
  }

  .photo-box {
    width: 120px;
    justify-self: end;
  }

  .fields-grid--mixed,
  .fields-grid--readable,
  .meta-row {
    grid-template-columns: 1fr;
  }

  .field-cell--sm,
  .field-cell--lg,
  .field-cell--full {
    grid-column: 1 / -1;
  }
}
</style>

<style lang="scss">
@media print {
  html,
  body,
  #app,
  .content-wrapper,
  .container,
  .container-fluid {
    width: 100% !important;
    max-width: 100% !important;
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    overflow: visible !important;
  }

  body * {
    overflow: visible !important;
  }
}
</style>
